import { useRef, useEffect, useLayoutEffect } from 'haunted/lib/core';
import KeenSlider from 'keen-slider';
import HTMLParsedElement from 'html-parsed-element';

import { component } from '../../utils';
import { HTMLParsedChildrenElement } from '../../utils/html-children-parsed';
import { SliderEvent, useSlider } from '../slider/slider.context';

import './slider-slides.css';

function SliderSlides(el: HTMLParsedChildrenElement): null {
  const initialSlide = 0;
  const keenSlider = useRef<KeenSlider | null>(null);
  const sliderContext = useSlider();

  useLayoutEffect(() => {
    el.classList.add('keen-slider');
  }, []);

  useEffect(() => {
    if (!el.childrenParsed || !('emitterRef' in sliderContext)) {
      return;
    }

    // Get slides
    const slides = el.querySelectorAll('.keen-slider__slide');

    // Initialize keen slider
    const slider = new KeenSlider(el, {
      slides,
      initial: initialSlide,
      spacing: 8,
      slideChanged(_slider) {
        const currentSlide = _slider.details().relativeSlide;
        sliderContext.emitterRef.current.emit('keenSlideChanged', {
          currentSlide,
        });
      },
      dragStart() {
        sliderContext.emitterRef.current.emit('keenDragStart');
      },
    });
    keenSlider.current = slider;

    // Listen for external events
    function moveToSlideHandler(event: SliderEvent['moveToSlide']) {
      keenSlider.current.moveToSlide(event.slide);
    }

    function nextSlideHandler() {
      keenSlider.current.next();
    }

    function autoNextSlide() {
      // Check if we are at the end, then run from the beginning
      const details = keenSlider.current.details();
      if (details.absoluteSlide === details.size - 1) {
        keenSlider.current.moveToSlide(0);
      } else {
        keenSlider.current.next();
      }
    }

    sliderContext.emitterRef.current.on('moveToSlide', moveToSlideHandler);
    sliderContext.emitterRef.current.on('nextSlide', nextSlideHandler);
    sliderContext.emitterRef.current.on('autoNextSlide', autoNextSlide);

    return () => {
      // Cleanup listener
      sliderContext.emitterRef.current.off('moveToSlide', moveToSlideHandler);
      sliderContext.emitterRef.current.off('nextSlide', nextSlideHandler);
      sliderContext.emitterRef.current.off('autoNextSlide', autoNextSlide);

      // Unmount slider
      slider.destroy();
    };
    // @ts-ignore - since only the emitter is used
  }, [sliderContext.emitterRef, el.childrenParsed]);

  return null;
}

export const SliderSlidesElement = HTMLParsedElement.withParsedCallback(
  // @ts-ignore
  component(SliderSlides, {
    useShadowDOM: false,
    baseElement: HTMLParsedChildrenElement,
  })
);
